import React, { useState, useEffect, useCallback } from "react";
import { X, Loader2 } from "lucide-react";
import axios from "axios";
import { SessionManager } from "../components/SessionManager";
import { PasswordChangeModal } from "../components/PasswordChangeModal";
import OnboardingWizard from "../components/OnboardingWizard";
import LoginForm from "../components/LoginForm";
import BookingModal from "../components/BookingModal";
import IncentivesModal from "../components/IncentivesModal";
// Extracted admin sub-components
import AdminHeader from "../components/admin/AdminHeader";
import useAdminTheme from "../components/admin/useAdminTheme";
import DataCleanTab from "../components/admin/DataCleanTab";
import DecoratorRegistrationsTab from "../components/admin/DecoratorRegistrationsTab";
import AdminSidebar from "../components/admin/AdminSidebar";
import AdminSearchBar from "../components/admin/AdminSearchBar";
import BookingsCalendarTab from "../components/admin/BookingsCalendarTab";
// Tab components
import TeamHubTab from "../components/TeamHubTab";
import DoozyGuardTab from "../components/doozyguard/DoozyGuardTab";
import DoozyBossTab from "../components/doozyboss/DoozyBossTab";
import DoozyHRTab from "../components/admin/DoozyHRTab";
import AccountsTab from "../components/admin/AccountsTab";
import DoozyAdTab from "../components/admin/DoozyAdTab";
import DoozyAcademy from "../components/admin/DoozyAcademy";
import AdminAIGalleryTab from "../components/admin/AdminAIGalleryTab";
import FinanceTab from "../components/FinanceTab";
import { VendorTab } from "../components/VendorTab";
import MaterialRatesTab from "../components/admin/MaterialRatesTab";
import ReferralsTab from "../components/ReferralsTab";
import CRMTab from "../components/CRMTab";
import SocialReferralTab from "../components/SocialReferralTab";
import OpsManagementTab from "../components/OpsManagementTab";
import OffersTab from "../components/OffersTab";
import SecurityDashboard from "../components/SecurityDashboard";
import SalesManagerTab from "../components/SalesManagerTab";
import LiveTrackerMap from "../components/LiveTrackerMap";
import RBACManagementTab from "../components/RBACManagementTab";
import SalesExecDashboard from "../components/SalesExecDashboard";
import PincodeManagementTab from "../components/PincodeManagementTab";
import BotTrainingTab from "../components/BotTrainingTab";
import DoozyBotTab from "../components/DoozyBotTab";
import DoozyBotNotifications from "../components/DoozyBotNotifications";
import CreatorAnalyticsTab from "../components/CreatorAnalyticsTab";
import AIGalleryTab from "../components/AIGalleryTab";
import ReferenceGalleryTab from "../components/ReferenceGalleryTab";
import MagicScaleTab from "../components/admin/MagicScaleTab";
import SmartAnalyticsTab from "../components/SmartAnalyticsTab";
import SmartSchedulerTab from "../components/SmartSchedulerTab";
import BookingCapacityPanel from "../components/BookingCapacityPanel";
import RevenueProjectionWidget from "../components/RevenueProjectionWidget";
import TrainingTab from "../components/admin/TrainingTab";
import StaffAssistantWidget from "../components/StaffAssistantWidget";
import InternalChatWidget from "../components/InternalChatWidget";
import PolicyResignBanner from "../components/PolicyResignBanner";
import { logger } from "../utils/logger";
import { adminRefreshBus } from "../utils/adminRefreshBus";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ auth, onLogout, onChangePassword }) => {
  const { theme, setTheme, isDark } = useAdminTheme();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [bookingCounts, setBookingCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [incentivesModalOpen, setIncentivesModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("calendar");
  const [salesExecutives, setSalesExecutives] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [newBookingCount, setNewBookingCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const mainRef = React.useRef(null);

  // Scroll to top on tab change
  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [activeTab]);

  const allowedTabs = auth.allowed_tabs || ["calendar", "crm", "tracker", "offers", "referrals", "ops", "social", "team", "doozyhr", "doozyguard", "doozyboss", "security", "finance", "sales_manager", "pincodes", "bot_training", "doozybot", "creators_analytics", "ai_gallery", "ai_creations"];

  // withCredentials=true sends the HttpOnly cookie (primary auth).
  // The in-memory token is ALSO sent as a header as a belt-and-suspenders fallback
  // for production setups where cookies may not flow (e.g. cross-subdomain API).
  const authAxios = axios.create({
    withCredentials: true,
    headers: auth.token ? { 'X-Admin-Auth': auth.token } : {},
  });

  const fetchSalesExecutives = useCallback(async () => {
    try { const r = await authAxios.get(`${API_URL}/api/admin/sales-executives`); setSalesExecutives(r.data); } catch (err) { logger.error('[Admin] Fetch sales executives error:', err.message); }
  }, [auth]);

  const handleAddExecutive = async (name) => {
    try { await authAxios.post(`${API_URL}/api/admin/sales-executives`, { name }); await fetchSalesExecutives(); }
    catch { setError("Failed to add sales executive"); }
  };

  const handleDeleteExecutive = async (execId) => {
    if (!window.confirm("Delete this sales executive?")) return;
    try { await authAxios.delete(`${API_URL}/api/admin/sales-executives/${execId}`); await fetchSalesExecutives(); }
    catch { setError("Failed to delete sales executive"); }
  };

  const fetchBookings = useCallback(async () => {
    try { const r = await authAxios.get(`${API_URL}/api/admin/bookings`); setBookings(r.data); }
    catch { setError("Failed to fetch bookings"); }
  }, [auth]);

  const fetchAvailability = useCallback(async () => {
    try {
      const y = currentMonth.getFullYear(), m = currentMonth.getMonth() + 1;
      const r = await axios.get(`${API_URL}/api/availability/${y}/${m}`);
      setBookingCounts(r.data.booking_counts || {});
    } catch (err) { logger.error('[Admin] Fetch availability error:', err.message); }
  }, [currentMonth]);

  useEffect(() => {
    const load = async () => { setLoading(true); await Promise.all([fetchBookings(), fetchAvailability(), fetchSalesExecutives()]); setLoading(false); };
    load();
  }, [fetchBookings, fetchAvailability, fetchSalesExecutives]);

  // Poll for new bookings every 15 seconds
  useEffect(() => {
    const fetchNewCount = async () => {
      try {
        const r = await authAxios.get(`${API_URL}/api/admin/bookings/new-count`);
        setNewBookingCount(r.data.new_count || 0);
      } catch (err) { logger.error('[Admin] Fetch new count error:', err.message); }
    };
    fetchNewCount();
    const interval = setInterval(fetchNewCount, 15000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // Attendance heartbeat — keep today's row alive; backend auto-closes idle sessions
  useEffect(() => {
    const ping = () => {
      authAxios.post(`${API_URL}/api/attendance/heartbeat`, {}).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 5 * 60 * 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const handleSaveBooking = async (formData, bookingId) => {
    try {
      if (bookingId) await authAxios.put(`${API_URL}/api/admin/bookings/${bookingId}`, formData);
      else await authAxios.post(`${API_URL}/api/admin/bookings`, formData);
      await Promise.all([fetchBookings(), fetchAvailability()]);
      adminRefreshBus.emit("bookings");
      setModalOpen(false); setSelectedBooking(null); setSelectedDate(null);
    } catch (err) { setError(err.response?.data?.detail || "Failed to save booking"); }
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try { await authAxios.delete(`${API_URL}/api/admin/bookings/${id}`); await Promise.all([fetchBookings(), fetchAvailability()]); adminRefreshBus.emit("bookings"); }
    catch { setError("Failed to delete booking"); }
  };

  const handleMarkCompleted = async (id) => {
    try { await authAxios.put(`${API_URL}/api/admin/bookings/${id}/mark-completed`); await fetchBookings(); adminRefreshBus.emit("bookings"); }
    catch { setError("Failed to mark as completed"); }
  };

  const handleResendInvoice = async (id) => {
    try { const r = await authAxios.post(`${API_URL}/api/admin/bookings/${id}/resend-invoice`); alert(r.data?.message || "Invoice resent!"); await fetchBookings(); }
    catch (err) { setError(err.response?.data?.detail || "Failed to resend invoice"); }
  };

  const handleResendConfirmation = async (id) => {
    try { const r = await authAxios.post(`${API_URL}/api/admin/bookings/${id}/resend-confirmation`); alert(r.data?.message || "Confirmation resent via Email + WhatsApp!"); }
    catch (err) { setError(err.response?.data?.detail || "Failed to resend confirmation"); }
  };

  const handleCancelBooking = async (id) => {
    const reason = prompt("Reason for cancellation:");
    if (reason === null) return;
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/bookings/${id}/cancel`, { reason: reason || "Admin cancelled" });
      const refundLine = r.data?.refund ? ` | Refund eligible: Rs ${r.data.refund.refund_amount}` : "";
      const autoRefund = r.data?.auto_refund;
      let autoLine = "";
      if (autoRefund?.refund_id) {
        autoLine = `\n\n✅ Razorpay refund initiated automatically:\n• Refund ID: ${autoRefund.refund_id}\n• Amount: Rs ${autoRefund.amount_inr}\n• Status: ${autoRefund.status}\n• Customer notified via WhatsApp.`;
      } else if (r.data?.refund?.refund_amount > 0) {
        autoLine = "\n\n⚠ Auto-refund didn't fire (no captured Razorpay payment_id, or already refunded). Check refund status from the Refunds admin endpoint.";
      }
      alert(r.data?.message + refundLine + autoLine);
      await fetchBookings();
    } catch (err) { setError(err.response?.data?.detail || "Failed to cancel booking"); }
  };

  const handleExportCSV = async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.setAttribute('download', 'doozygo_bookings.csv');
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { setError("Failed to export CSV."); }
  };

  const openAddModal = (date = null) => { setSelectedBooking(null); setSelectedDate(date || null); setModalOpen(true); };
  const openEditModal = (booking) => { setSelectedBooking(booking); setSelectedDate(null); setModalOpen(true); };

  // Filter bookings for the calendar list view.
  // Upcoming = future-dated + fully PAID. Unpaid attempts must never show up
  // in the "Upcoming Bookings" widget — they aren't commitments. Admins
  // chase them from the dedicated Bookings table (which shows everything).
  const upcomingBookings = bookings
    .filter(b => {
      if ((b.payment_status || "").toLowerCase() !== "paid") return false;
      if (dateFrom && b.date < dateFrom) return false;
      if (dateTo && b.date > dateTo) return false;
      if (!dateFrom && !dateTo && new Date(b.date) < new Date(new Date().toDateString())) return false;
      if (statusFilter !== "all" && b.booking_status !== statusFilter) return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (b.customer_name || "").toLowerCase().includes(q)
        || (b.customer_phone || "").toLowerCase().includes(q)
        || (b.package || "").toLowerCase().includes(q)
        || (b.occasion || "").toLowerCase().includes(q)
        || (b.id || "").toLowerCase().includes(q);
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className={`min-h-screen flex admin-shell ${isDark ? "theme-dark" : "theme-light"}`} data-testid="admin-dashboard">
      <AdminSidebar
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setActiveTab(tab);
          if ((tab === 'calendar' || tab === 'crm') && newBookingCount > 0) {
            authAxios.post(`${API_URL}/api/admin/bookings/mark-viewed`).then(() => {
              setNewBookingCount(0);
              if (tab === 'calendar') fetchBookings();
            }).catch(() => {});
          }
        }}
        allowedTabs={allowedTabs}
        auth={auth}
        newBookingCount={newBookingCount}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"}`}>
        <AdminHeader auth={auth} allowedTabs={allowedTabs} activeTab={activeTab} onExportCSV={handleExportCSV}
          onOpenIncentives={() => setIncentivesModalOpen(true)} onChangePassword={onChangePassword} onLogout={onLogout}
          theme={theme} setTheme={setTheme}
          onOpenDoozybot={() => setActiveTab("doozybot")} />

        <main ref={mainRef} className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Mobile sidebar toggle */}
          <button onClick={() => setSidebarCollapsed(false)} className="lg:hidden mb-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 text-sm text-[#475569]" data-testid="mobile-menu-btn">
            <span className="w-4 h-0.5 bg-[#475569] block" /><span className="w-3 h-0.5 bg-[#475569] block" /><span className="w-4 h-0.5 bg-[#475569] block" />
            <span className="ml-1 text-xs font-medium">Menu</span>
          </button>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-6 flex items-center justify-between">
              {error} <button onClick={() => setError("")}><X size={18} /></button>
            </div>
          )}

          <AdminSearchBar activeTab={activeTab} searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />

        <div key={activeTab} className="admin-fade-in">
        {activeTab === "calendar" && (
          <>
            <RevenueProjectionWidget authAxios={authAxios} />
            <BookingCapacityPanel authAxios={authAxios} />
            <BookingsCalendarTab auth={auth} authAxios={authAxios} loading={loading}
              currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} bookingCounts={bookingCounts}
              upcomingBookings={upcomingBookings}
              onAddBooking={openAddModal} onEditBooking={openEditModal} onDeleteBooking={handleDeleteBooking}
              onMarkCompleted={handleMarkCompleted} onCancelBooking={handleCancelBooking} onResendInvoice={handleResendInvoice} onResendConfirmation={handleResendConfirmation} />
          </>
        )}

        {activeTab === "offers" && <OffersTab auth={auth} authAxios={authAxios} />}
        {activeTab === "referrals" && <ReferralsTab auth={auth} authAxios={authAxios} />}
        {activeTab === "crm" && <CRMTab auth={auth} authAxios={authAxios} searchQuery={searchQuery} bookings={bookings} onRefresh={fetchBookings} />}
        {activeTab === "tracker" && <LiveTrackerMap auth={auth} authAxios={authAxios} />}
        {activeTab === "ops" && <OpsManagementTab auth={auth} authAxios={authAxios} searchQuery={searchQuery} />}
        {activeTab === "social" && <SocialReferralTab auth={auth} authAxios={authAxios} />}
        {activeTab === "team" && <TeamHubTab auth={auth} authAxios={authAxios} />}
        {activeTab === "doozyhr" && <DoozyHRTab auth={auth} authAxios={authAxios} setActiveTab={setActiveTab} />}
        {activeTab === "accounts" && <AccountsTab authAxios={authAxios} />}
        {activeTab === "doozyad" && <DoozyAdTab auth={auth} authAxios={authAxios} />}
        {activeTab === "ai_creations" && <AdminAIGalleryTab auth={auth} />}
        {activeTab === "doozyguard" && <DoozyGuardTab authAxios={authAxios} />}
        {activeTab === "doozyboss" && <DoozyBossTab authAxios={authAxios} />}
        {activeTab === "security" && <SecurityDashboard auth={auth} authAxios={authAxios} />}
        {activeTab === "finance" && (
          <>
            <RevenueProjectionWidget authAxios={authAxios} />
            <FinanceTab auth={auth} authAxios={authAxios} />
          </>
        )}
        {activeTab === "sales_manager" && <SalesManagerTab auth={auth} authAxios={authAxios} searchQuery={searchQuery} />}
        {activeTab === "sales_dashboard" && <SalesExecDashboard auth={auth} authAxios={authAxios} />}
        {activeTab === "academy" && <DoozyAcademy authAxios={authAxios} />}
        {activeTab === "vendors" && <VendorTab auth={auth} authAxios={authAxios} searchQuery={searchQuery} />}
        {activeTab === "materials" && <MaterialRatesTab auth={auth} authAxios={authAxios} />}
        {activeTab === "decorator_registrations" && <DecoratorRegistrationsTab auth={auth} authAxios={authAxios} />}
        {activeTab === "pincodes" && <PincodeManagementTab auth={auth} authAxios={authAxios} />}
        {activeTab === "bot_training" && <BotTrainingTab auth={auth} authAxios={authAxios} />}
        {activeTab === "doozybot" && <DoozyBotTab auth={auth} authAxios={authAxios} />}
        {activeTab === "creators_analytics" && <CreatorAnalyticsTab authAxios={authAxios} />}
        {activeTab === "ai_gallery" && <AIGalleryTab authAxios={authAxios} />}
        {activeTab === "reference_gallery" && <ReferenceGalleryTab authAxios={authAxios} />}
        {activeTab === "magic_scale" && <MagicScaleTab authAxios={authAxios} />}
        {activeTab === "smart_analytics" && <SmartAnalyticsTab auth={auth} authAxios={authAxios} />}
        {activeTab === "smart_scheduler" && <SmartSchedulerTab authAxios={authAxios} />}
        {activeTab === "training" && <TrainingTab authAxios={authAxios} />}
        {activeTab === "rbac" && <RBACManagementTab auth={auth} authAxios={authAxios} />}
        {activeTab === "data_clean" && <DataCleanTab auth={auth} authAxios={authAxios} />}
        </div>
        </main>
      </div>

      <BookingModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setSelectedBooking(null); setSelectedDate(null); }}
        onSave={handleSaveBooking} booking={selectedBooking} selectedDate={selectedDate}
        salesExecutives={salesExecutives} onAddExecutive={handleAddExecutive} onDeleteExecutive={handleDeleteExecutive} />
      <IncentivesModal isOpen={incentivesModalOpen} onClose={() => setIncentivesModalOpen(false)} auth={auth} />
      <DoozyBotNotifications authAxios={authAxios} />
      {/* Policy re-sign banner — appears when admin pushes a new policy version */}
      <PolicyResignBanner authAxios={authAxios} employeeName={auth?.name || ""} />
      {/* DoozyBot Training Assistant — floats bottom-left, available to all logged-in admin/team users */}
      <StaffAssistantWidget authAxios={authAxios} roleLabel={auth?.role || "Team"} />
      {/* Internal Team Chat — floats bottom-right (below the public DoozyBot) */}
      <InternalChatWidget authAxios={authAxios} />
    </div>
  );
};

// Main Admin Component — Auth wrapper
const Admin = () => {
  const [auth, setAuth] = useState(null);
  const [logoutReason, setLogoutReason] = useState("");
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);

  // Persist metadata (localStorage) + JWT (sessionStorage) for refresh-survival.
  // Why we persist the token at all:
  //   On the live doozygo.com domain the HttpOnly cookie can be silently dropped
  //   by Safari/iOS ITP and by certain reverse-proxy / cross-origin setups, so
  //   relying on the cookie alone caused admins to be logged out on every refresh.
  //   The X-Admin-Auth header fallback (already supported by the backend) needs
  //   the JWT in JS-readable storage to survive a page reload.
  // Why sessionStorage (was localStorage): it survives a same-tab reload but is
  //   cleared on tab/browser close and is isolated per-tab, shrinking the XSS
  //   persistence window. The HttpOnly cookie remains the primary credential;
  //   sessionStorage is only the Safari/iOS rescue lane.
  const TOKEN_KEY = 'doozygo_admin_jwt';
  const saveToken = (t) => {
    if (!t) return;
    try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* storage blocked */ }
  };
  const readToken = () => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  };
  const clearToken = () => {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore legacy */ }
  };
  const persistMeta = (a) => {
    const meta = {
      username: a.username, role: a.role, name: a.name,
      allowed_tabs: a.allowed_tabs || [],
      role_label: a.role_label || a.role,
      session_timeout_minutes: a.session_timeout_minutes || 30,
      force_password_change: !!a.force_password_change,
    };
    localStorage.setItem('doozygo_admin', JSON.stringify(meta));
    localStorage.setItem('doozygo_last_activity', Date.now().toString());
    saveToken(a.token);
  };

  const handleLogin = (credentials) => {
    // credentials arrives with .token from LoginForm; cookie was already set by backend.
    // We keep the token in React memory AND in sessionStorage so the X-Admin-Auth
    // header fallback survives same-tab refreshes when the cookie is stripped.
    const { token, ...meta } = credentials;
    setAuth({ ...meta, token: token || "" });
    setLogoutReason("");
    persistMeta({ ...meta, token: token || "" });
  };

  const handleLogout = useCallback(async (reason) => {
    try { await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }); }
    catch (err) { logger.warn('[Admin] Logout error:', err?.message); }
    setAuth(null); setShowPasswordChange(false); setOnboardingStatus(null);
    localStorage.removeItem('doozygo_admin');
    localStorage.removeItem('doozygo_last_activity');
    clearToken();
    if (reason === "session_timeout") setLogoutReason("Your session has expired due to inactivity. Please sign in again.");
    else if (reason === "token_expired") setLogoutReason("Your session has expired. Please sign in again.");
    else setLogoutReason("");
  }, []);

  const handlePasswordChangeSuccess = (newToken) => {
    if (auth) {
      const updated = { ...auth, token: newToken || "", force_password_change: false };
      setAuth(updated);
      persistMeta(updated);
    }
    setShowPasswordChange(false);
  };

  // Bootstrap: restore metadata + validate cookie/header session
  useEffect(() => {
    const saved = localStorage.getItem('doozygo_admin');
    if (!saved) { setBootstrapping(false); return; }
    let meta;
    try { meta = JSON.parse(saved); } catch { localStorage.removeItem('doozygo_admin'); setBootstrapping(false); return; }
    // Header fallback when the HttpOnly cookie was dropped (Safari/ITP, cross-site).
    // Token lives in sessionStorage; migrate any legacy localStorage token once.
    let savedToken = readToken();
    if (!savedToken) {
      let legacy = "";
      try { legacy = localStorage.getItem(TOKEN_KEY) || ""; } catch { legacy = ""; }
      if (legacy) { saveToken(legacy); savedToken = legacy; }
      try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    }
    const headers = savedToken ? { "X-Admin-Auth": savedToken } : {};
    axios.get(`${API_URL}/api/auth/validate-session`, { withCredentials: true, headers })
      .then((r) => {
        const freshToken = r.data?.token || savedToken;
        const next = {
          ...meta,
          token: freshToken,
          force_password_change: r.data?.force_password_change || meta.force_password_change,
        };
        setAuth(next);
        if (freshToken) saveToken(freshToken);
      })
      .catch(() => {
        localStorage.removeItem('doozygo_admin');
        clearToken();
      })
      .finally(() => setBootstrapping(false));
  }, []);

  useEffect(() => {
    if (!auth || auth.role === "admin") { setOnboardingStatus(null); return; }
    const check = async () => {
      setCheckingOnboarding(true);
      try {
        const headers = auth.token ? { "X-Admin-Auth": auth.token } : {};
        const r = await fetch(`${API_URL}/api/onboarding/status`, { credentials: 'include', headers });
        if (r.ok) {
          const d = await r.json();
          // Fail-secure: if we can't determine the status, default to pending so
          // the onboarding wizard shows. Silent fall-through to the dashboard
          // (the old bug) leaves unsigned policies unsigned.
          setOnboardingStatus(d.verification_status || "pending_verification");
        } else {
          logger.warn('[Admin] Onboarding status HTTP', r.status, '— defaulting to pending_verification');
          setOnboardingStatus("pending_verification");
        }
      } catch (err) {
        logger.error('[Admin] Onboarding status error:', err.message);
        // Network failure → fail-secure: show wizard instead of leaking into the dashboard
        setOnboardingStatus("pending_verification");
      }
      setCheckingOnboarding(false);
    };
    check();
  }, [auth]);

  if (bootstrapping) return <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center"><Loader2 className="animate-spin text-[#DC283C]" size={40} /></div>;
  if (!auth) return <LoginForm onLogin={handleLogin} logoutReason={logoutReason} />;
  if (auth.force_password_change && auth.role !== "admin") return <PasswordChangeModal auth={auth} isForced={true} onSuccess={handlePasswordChangeSuccess} onCancel={() => {}} />;
  if (checkingOnboarding) return <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center"><Loader2 className="animate-spin text-[#DC283C]" size={40} /></div>;
  if (auth.role !== "admin" && onboardingStatus && onboardingStatus !== "approved") return <OnboardingWizard auth={auth} onLogout={handleLogout} />;

  return (
    <>
      <SessionManager auth={auth} onLogout={handleLogout} timeoutMinutes={auth.session_timeout_minutes || 30} />
      {showPasswordChange && <PasswordChangeModal auth={auth} isForced={false} onSuccess={handlePasswordChangeSuccess} onCancel={() => setShowPasswordChange(false)} />}
      <AdminDashboard auth={auth} onLogout={() => handleLogout("manual")} onChangePassword={() => setShowPasswordChange(true)} />
    </>
  );
};

export default Admin;
